import React, { useCallback, useMemo } from "react";
import { WidgetAddMaster } from "./WidgetAddMaster";
import { WADrawer, WABaseScreenParams, WACategoryKey } from "./WANavigation";
import { Box } from "arkh";
import {
  DrawerContentComponentProps,
  DrawerNavigationOptions,
} from "@react-navigation/drawer";
import { WACategoryChecklists } from "./Categories/WACategoryChecklists/WACategoryChecklists";
import { WACategoryWeather } from "./Categories/WACategoryWeather";
import { WACategoryIntegrations } from "./Categories/WACategoryIntegrations/WACategoryIntegrations";
import { WACategoryTeamSchedule } from "./Categories/WACategoryTeamSchedule/WACategoryTeamSchedule";
import { WACategoryNote } from "./Categories/WACategoryNote/WACategoryNote";
import { WACategoryKPI } from "./Categories/WACategoryKPI/WACategoryKPI";
import { WidgetSizeT } from "../localTypes/WidgetSizeT";
import { EmptyPositionT } from "../EmptyPositions/EmptyPosType";

import { WAFakeHeader } from "./WAFakeHeader";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { WACategoryFile } from "./Categories/WACategoryFile/WACategoryFile";
import { WACategoryOrg } from "./Categories/WACategoryOrg";
import { Platform } from "react-native";
import { useWACategoryLayout } from "./utils/useWACategoryLayout";
import { WEB_MODAL_DEFAULT_WIDTH } from "@/src/TopLevelNavigators/UserLoggedIn/WEB_MODAL_DEFAULT_WIDTH";
import { WACategoryTodoCategories } from "./Categories/WACategoryTodoCategories/WACategoryTodoCategories";
import { WACategoryDefaultTodoCategory } from "./Categories/WACategoryDefaultTodoCategory/WACategoryDefaultTodoCategory";
import { WACategoryCalendar } from "./Categories/WACategoryCalendar/WACategoryCalendar";
import { WACategorySignals } from "./Categories/WACategorySignals";
import { WACategoryEmployeeRoles } from "./Categories/WACategoryEmployeeRoles";

type Props = {
  maxSize: WidgetSizeT;
  storecastId: string;
  defaultTeams: string[];
  position: EmptyPositionT;
  onClose(): void;
};

const DRAWER_WIDTH = 290;

export const WADrawerScreenOptions: DrawerNavigationOptions = {
  headerShown: false,
  drawerType: "permanent",
  drawerStyle: {
    width: DRAWER_WIDTH,
  },
  headerStatusBarHeight: 0,
};

export function WARegular(props: Props) {
  const paddingHorizontal = useWACategoryLayout().paddingHorizontal;
  const unitSize = useMemo(() => {
    return (
      ((Platform.OS === "web" ? WEB_MODAL_DEFAULT_WIDTH : 704) - DRAWER_WIDTH) /
        2 -
      paddingHorizontal
    );
  }, [paddingHorizontal]);
  const renderDrawerContent = useCallback(
    (drawerProps: DrawerContentComponentProps) => {
      return (
        <WADrawerContent
          {...drawerProps}
          defaultTeams={props.defaultTeams}
          onClose={props.onClose}
          maxSize={props.maxSize}
          unitSize={unitSize}
          position={props.position}
          storecastId={props.storecastId}
        />
      );
    },
    [
      props.defaultTeams,
      props.onClose,
      props.maxSize,
      props.position,
      props.storecastId,
      unitSize,
    ]
  );
  const initialParams = useMemo(
    () => ({
      storecastId: props.storecastId,
      unitSize,
      maxSize: props.maxSize,
      defaultTeams: props.defaultTeams,
      position: props.position,
    }),
    [
      props.defaultTeams,
      props.maxSize,
      props.position,
      props.storecastId,
      unitSize,
    ]
  );

  return (
    <WADrawer.Navigator
      defaultStatus="open"
      drawerContent={renderDrawerContent}
      screenOptions={WADrawerScreenOptions}
    >
      <WADrawer.Screen
        name="WACategoryTeamSchedule"
        component={WACategoryTeamSchedule}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        component={WACategoryChecklists}
        name="WACategoryChecklists"
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryCalendar"
        component={WACategoryCalendar}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryWeather"
        component={WACategoryWeather}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryNote"
        component={WACategoryNote}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryTodoCategories"
        component={WACategoryTodoCategories}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryDefaultTodoCategory"
        component={WACategoryDefaultTodoCategory}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryKPI"
        component={WACategoryKPI}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryIntegrations"
        component={WACategoryIntegrations}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryFile"
        component={WACategoryFile}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryOrgInfo"
        component={WACategoryOrg}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategorySignals"
        component={WACategorySignals}
        initialParams={initialParams}
      />
      <WADrawer.Screen
        name="WACategoryEmployeeRoles"
        component={WACategoryEmployeeRoles}
        initialParams={initialParams}
      />
    </WADrawer.Navigator>
  );
}

function WADrawerContent(
  props: {
    storecastId: string;
    defaultTeams: string[];
    position: EmptyPositionT;
    unitSize: number;
    maxSize: WidgetSizeT;
    onClose(): void;
  } & DrawerContentComponentProps
) {
  const intl_tokens = useIntlTokens();

  const onPress = useCallback(
    (
      input:
        | { key: Exclude<WACategoryKey, "WACategoryDefaultTodoCategory"> }
        | { key: "WACategoryDefaultTodoCategory"; categoryId: string }
    ) => {
      if (input.key === "WACategoryDefaultTodoCategory") {
        props.navigation.navigateDeprecated(input.key, {
          storecastId: props.storecastId,
          unitSize: props.unitSize,
          maxSize: props.maxSize,
          defaultTeams: props.defaultTeams,
          position: props.position,
          categoryId: input.categoryId,
        } as WABaseScreenParams & { categoryId: string });
      } else if (input.key === "WACategoryTodoCategories") {
        props.navigation.navigateDeprecated("WACategoryTodoCategories", {
          screen: "WACategoryTodoCategoriesSelectCategory",
          params: {
            storecastId: props.storecastId,
            unitSize: props.unitSize,
            maxSize: props.maxSize,
            defaultTeams: props.defaultTeams,
            position: props.position,
          },
        });
      } else {
        props.navigation.navigateDeprecated(input.key, {
          storecastId: props.storecastId,
          unitSize: props.unitSize,
          maxSize: props.maxSize,
          defaultTeams: props.defaultTeams,
          position: props.position,
        } as WABaseScreenParams);
      }
    },
    [
      props.defaultTeams,
      props.maxSize,
      props.navigation,
      props.position,
      props.storecastId,
      props.unitSize,
    ]
  );

  const selectedRouteName = useMemo(() => {
    return props.state.routeNames[props.state.index] as WACategoryKey;
  }, [props.state.index, props.state.routeNames]);

  return (
    <Box style={{ flex: 1, borderRightWidth: 1 }} borderColor="border-strong">
      <WAFakeHeader title={intl_tokens.AddWidget} />
      <WidgetAddMaster selected={selectedRouteName} onPress={onPress} />
    </Box>
  );
}
